//----------------------------------------------------------------------------------------------------------------------
// GameLevel Class - Default Level Implementation
//----------------------------------------------------------------------------------------------------------------------

import {
    type AbstractMesh,
    ArcRotateCamera,
    type Camera,
    Color3,
    CubeTexture,
    DirectionalLight,
    FreeCamera,
    HDRCubeTexture,
    HemisphericLight,
    MeshBuilder,
    PBRMaterial,
    PointLight,
    Quaternion,
    RectAreaLight,
    Scene,
    SoundState,
    SpotLight,
    StandardMaterial,
    type StaticSound,
    Texture,
    TransformNode,
    UniversalCamera,
    Vector3,
} from '@babylonjs/core';
import { GeospatialCamera } from '@babylonjs/core/Cameras/geospatialCamera';
import { ClusteredLightContainer } from '@babylonjs/core/Lights/Clustered/clusteredLightContainer';

import type {
    CameraDefinition,
    ColorConfig,
    LevelConfig,
    LevelContext,
    LightDefinition,
    SpawnDefinition,
} from '../interfaces/level.ts';
import { applyPostProcessing } from '../handlers/postProcessing.ts';
import type { ColliderDebugConfig } from '../managers/colliderDebug.ts';
import { getCanonicalTransform, toQuatObject, toVec3Object, toVector3 } from '../utils/vectors.ts';

import type { GameEntity } from './entity.ts';
import { Level } from './level.ts';
import { OutlineManager } from '../managers/outline.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Metadata collected from a spawn point node.
 * Transforms are normalized to canonical Babylon space — the source level's __root__
 * conversion is removed so these values are clean for any entity type.
 */
interface SpawnPointData
{
    name : string;
    position : Vector3;
    rotation : Quaternion;
    scaling : Vector3;
    node : TransformNode;
}

/**
 * Metadata collected from an entity node
 */
interface EntityNodeData
{
    type : string;
    node : TransformNode;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Default Level implementation that loads from YAML configuration.
 * Supports property handlers for processing scene node metadata.
 */
export class GameLevel extends Level
{
    /** The level configuration */
    protected _config : LevelConfig;

    /** Collected spawn points from the scene */
    protected _spawnPoints : SpawnPointData[] = [];

    /** Collected entity nodes from the scene */
    protected _entityNodes : EntityNodeData[] = [];

    /** Entities spawned by this level */
    protected _spawnedEntities : GameEntity[] = [];

    /** Level-scoped sounds created from config */
    protected _levelSounds = new Map<string, StaticSound>();

    /** Sounds that were playing before deactivation (for resume on activate) */
    private _playingSoundsBeforeDeactivate = new Set<string>();

    //------------------------------------------------------------------------------------------------------------------
    // Constructor
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create a GameLevel from a configuration object
     *
     * @param config
     * @param context
     */
    constructor(config : LevelConfig, context : LevelContext)
    {
        super(config, context);
        this._config = config;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Accessors
    //------------------------------------------------------------------------------------------------------------------

    get config() : LevelConfig
    {
        return this._config;
    }

    get spawnedEntities() : readonly GameEntity[]
    {
        return this._spawnedEntities;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Protected Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Build the scene by loading assets, processing node metadata, and spawning entities.
     */
    protected async buildScene() : Promise<Scene>
    {
        const { sceneEngine } = this.gameEngine.engines;
        const { assetManager } = this.gameEngine.managers;

        // Preload assets if configured
        if(this._config.preload && this._config.preload.length > 0)
        {
            this.$emitProgress(2, 'Preloading assets...');
            await assetManager.preload(this._config.preload);
        }

        // Create an empty scene and assign it early so property handlers can access it
        const scene = sceneEngine.createScene();
        this._scene = scene;

        // Enable physics if configured (must be done before loading scene with colliders)
        if(this._config.physics)
        {
            this.$emitProgress(5, 'Initializing physics...');
            await this._enablePhysics(scene);
        }

        // Load the scene file if specified
        if(this._config.scene)
        {
            this.$emitProgress(10, 'Loading scene file...');
            await this._loadSceneFile(scene);
        }

        // Set up environment (skybox + IBL) — before cameras so the background is ready
        if(this._config.environment)
        {
            this.$emitProgress(15, 'Setting up environment...');
            this._processEnvironment(scene);
        }

        // Process cameras (must happen before post-processing, which requires activeCamera)
        this._processCameras(scene);

        // Process lights (override Blender-imported lights, create new ones)
        this._processLights(scene);

        // Set up clustered lighting if configured (must be after _processLights)
        this._setupClustering(scene);

        // Apply post-processing effects if configured
        if(this._config.postProcessing)
        {
            const renderer = this._config.postProcessing.renderer ?? 'pipeline';
            this._log.info(`Applying post-processing with ${ renderer } renderer`);
            await applyPostProcessing(scene, this._config.postProcessing);
        }

        // Create outline manager for entity highlighting
        this.outlines = new OutlineManager(scene, this._config.outlines);

        // Process all nodes for property handlers
        this.$emitProgress(50, 'Processing scene properties...');
        await this._processNodeProperties(scene);

        // Process spawn points
        this.$emitProgress(70, 'Spawning entities...');
        await this._processSpawnPoints();

        // Process entity nodes
        this.$emitProgress(85, 'Configuring entities...');
        await this._processEntityNodes();

        // Load level-scoped sounds (last — everything else is ready)
        if(this._config.sounds)
        {
            this.$emitProgress(95, 'Loading sounds...');
            await this._processLevelSounds();
        }

        return scene;
    }

    /**
     * Import all meshes from the configured scene file (GLB/GLTF/Babylon) into the scene.
     */
    private async _loadSceneFile(scene : Scene) : Promise<void>
    {
        const scenePath = this._getScenePath();
        if(!scenePath)
        {
            return;
        }

        this._log.debug(`Loading scene file: ${ scenePath }`);

        // Set right-handed coordinate system before loading so loaders respect it
        if(this._isRightHanded())
        {
            scene.useRightHandedSystem = true;
            this._log.debug('Scene set to right-handed coordinate system');
        }

        try
        {
            await this.gameEngine.engines.sceneEngine.importMeshes([], scenePath, scene);
            this._log.debug('Scene file loaded successfully');
        }
        catch (error)
        {
            this._log.error(`Failed to load scene file: ${ scenePath }`, error);
            throw error;
        }
    }

    /**
     * Extract the scene file path from the config, which can be a string or SceneConfig object.
     */
    private _getScenePath() : string | undefined
    {
        if(!this._config.scene)
        {
            return undefined;
        }

        return typeof this._config.scene === 'string'
            ? this._config.scene
            : this._config.scene.path;
    }

    /**
     * Check if the scene config specifies a right-handed coordinate system.
     */
    private _isRightHanded() : boolean
    {
        if(!this._config.scene || typeof this._config.scene === 'string')
        {
            return false;
        }

        return this._config.scene.rightHanded === true;
    }

    /**
     * Set up the scene environment: IBL (image-based lighting) for PBR reflections and/or a visible skybox.
     */
    private _processEnvironment(scene : Scene) : void
    {
        const env = this._config.environment;
        if(!env) { return; }

        // Set up IBL for PBR reflections
        if(env.ibl)
        {
            scene.environmentTexture = this._createEnvironmentTexture(env.ibl, env.iblResolution ?? 256, scene);
            this._log.debug(`IBL set from: ${ env.ibl }`);
        }

        // Set up visible skybox
        if(env.skybox)
        {
            const ext = this._getFileExtension(env.skybox);

            if(ext === '.hdr' || ext === '.env')
            {
                // HDR/env skybox — use as IBL too if no separate IBL was specified
                if(!env.ibl)
                {
                    const res = env.iblResolution ?? 256;
                    scene.environmentTexture = this._createEnvironmentTexture(env.skybox, res, scene);
                }

                if(scene.environmentTexture)
                {
                    scene.createDefaultSkybox(scene.environmentTexture, true, env.skyboxSize ?? 1000, 0, false);
                }
            }
            else
            {
                // JPG/PNG — create a textured sphere
                const size = env.skyboxSize ?? 1000;
                const skybox = MeshBuilder.CreateSphere('skybox', { diameter: size, segments: 32 }, scene);

                const mat = new StandardMaterial('skybox-mat', scene);
                mat.backFaceCulling = false;
                mat.disableLighting = true;
                mat.emissiveTexture = new Texture(env.skybox, scene);
                skybox.material = mat;
                skybox.infiniteDistance = true;
            }

            this._log.debug(`Skybox set from: ${ env.skybox }`);
        }

        // Apply rotation to environment texture
        if(env.rotation !== undefined && scene.environmentTexture)
        {
            (scene.environmentTexture as HDRCubeTexture).rotationY = env.rotation;
        }
    }

    /**
     * Create an environment texture from an HDR or env file.
     */
    private _createEnvironmentTexture(path : string, resolution : number, scene : Scene) : CubeTexture | HDRCubeTexture
    {
        const ext = this._getFileExtension(path);

        if(ext === '.env')
        {
            return CubeTexture.CreateFromPrefilteredData(path, scene);
        }

        return new HDRCubeTexture(path, scene, resolution);
    }

    private _getFileExtension(path : string) : string
    {
        const dotIndex = path.lastIndexOf('.');
        return dotIndex >= 0 ? path.slice(dotIndex).toLowerCase() : '';
    }

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Process cameras: apply YAML overrides to imported cameras, create new ones, and set the active camera.
     * If no YAML cameras config exists, the first imported camera (if any) is activated.
     */
    private _processCameras(scene : Scene) : void
    {
        const cameraConfigs = this._config.cameras;
        const canvas = this.gameEngine.canvas as HTMLCanvasElement | null;

        if(!cameraConfigs)
        {
            // No YAML config — if the scene has imported cameras, activate the first one
            if(scene.cameras.length > 0)
            {
                scene.activeCamera = scene.cameras[0];

                if(canvas)
                {
                    scene.activeCamera.attachControl(canvas, true);
                }

                this._log.debug(`Activated imported camera: "${ scene.activeCamera.name }"`);
            }

            return;
        }

        let activeCamera : Camera | null = null;

        for(const [ name, config ] of Object.entries(cameraConfigs))
        {
            const existing = scene.getCameraByName(name);

            if(existing)
            {
                this._applyCameraConfig(existing, config);
                this._log.debug(`Applied overrides to camera: "${ name }"`);

                if(!activeCamera || config.active)
                {
                    activeCamera = existing;
                }
            }
            else if(config.type)
            {
                const camera = this._createCamera(name, config, scene);
                this._log.debug(`Created ${ config.type } camera: "${ name }"`);

                if(!activeCamera || config.active)
                {
                    activeCamera = camera;
                }
            }
            else
            {
                this._log.warn(
                    `Camera "${ name }" not found in scene and no type specified — skipping`
                );
            }
        }

        if(activeCamera)
        {
            scene.activeCamera = activeCamera;

            const activeDef = cameraConfigs[activeCamera.name];
            if(canvas && activeDef?.attachControl !== false)
            {
                activeCamera.attachControl(canvas, true);
            }
        }
    }

    /**
     * Create a new camera from a YAML definition.
     */
    private _createCamera(name : string, config : CameraDefinition, scene : Scene) : Camera
    {
        const pos = config.position ? toVector3(config.position) : Vector3.Zero();

        let camera : Camera;

        switch (config.type)
        {
            case 'arcRotate':
            {
                const target = config.target ? toVector3(config.target) : Vector3.Zero();

                camera = new ArcRotateCamera(
                    name,
                    config.alpha ?? Math.PI / 2,
                    config.beta ?? Math.PI / 3,
                    config.radius ?? 10,
                    target,
                    scene
                );
                break;
            }

            case 'universal':
            {
                camera = new UniversalCamera(name, pos, scene);
                break;
            }

            case 'geospatial':
            {
                if(!config.planetRadius)
                {
                    this._log.warn(`Geospatial camera "${ name }" requires planetRadius -- skipping`);
                    return new FreeCamera(name, Vector3.Zero(), scene);
                }

                const cam = new GeospatialCamera(name, scene, {
                    planetRadius: config.planetRadius,
                });

                if(config.center) { cam.center = toVector3(config.center); }
                if(config.yaw !== undefined) { cam.yaw = config.yaw; }
                if(config.pitch !== undefined) { cam.pitch = config.pitch; }
                if(config.radius !== undefined) { cam.radius = config.radius; }
                if(config.checkCollisions !== undefined) { cam.checkCollisions = config.checkCollisions; }

                if(config.radiusMin !== undefined) { cam.limits.radiusMin = config.radiusMin; }
                if(config.radiusMax !== undefined) { cam.limits.radiusMax = config.radiusMax; }
                if(config.pitchMin !== undefined) { cam.limits.pitchMin = config.pitchMin; }
                if(config.pitchMax !== undefined) { cam.limits.pitchMax = config.pitchMax; }
                if(config.yawMin !== undefined) { cam.limits.yawMin = config.yawMin; }
                if(config.yawMax !== undefined) { cam.limits.yawMax = config.yawMax; }

                camera = cam;
                break;
            }

            case 'free':
            default:
            {
                camera = new FreeCamera(name, pos, scene);
                break;
            }
        }

        this._applyCameraConfig(camera, config);
        return camera;
    }

    /**
     * Apply shared camera properties from a config to an existing camera.
     */
    private _applyCameraConfig(camera : Camera, config : CameraDefinition) : void
    {
        if(config.fov !== undefined) { camera.fov = config.fov; }
        if(config.position) { camera.position = toVector3(config.position); }
        if(config.minZ !== undefined) { camera.minZ = config.minZ; }
        if(config.maxZ !== undefined) { camera.maxZ = config.maxZ; }

        if(camera instanceof FreeCamera)
        {
            if(config.speed !== undefined) { camera.speed = config.speed; }
            if(config.rotation) { camera.rotation = toVector3(config.rotation); }
        }

        if(camera instanceof ArcRotateCamera)
        {
            if(config.target) { camera.setTarget(toVector3(config.target)); }
            if(config.lowerRadiusLimit !== undefined) { camera.lowerRadiusLimit = config.lowerRadiusLimit; }
            if(config.upperRadiusLimit !== undefined) { camera.upperRadiusLimit = config.upperRadiusLimit; }
            if(config.lowerBetaLimit !== undefined) { camera.lowerBetaLimit = config.lowerBetaLimit; }
            if(config.upperBetaLimit !== undefined) { camera.upperBetaLimit = config.upperBetaLimit; }
            if(config.wheelPrecision !== undefined) { camera.wheelPrecision = config.wheelPrecision; }
        }

        if(camera instanceof GeospatialCamera)
        {
            if(config.yaw !== undefined) { camera.yaw = config.yaw; }
            if(config.pitch !== undefined) { camera.pitch = config.pitch; }
            if(config.radius !== undefined) { camera.radius = config.radius; }
            if(config.center) { camera.center = toVector3(config.center); }
            if(config.checkCollisions !== undefined) { camera.checkCollisions = config.checkCollisions; }
            if(config.radiusMin !== undefined) { camera.limits.radiusMin = config.radiusMin; }
            if(config.radiusMax !== undefined) { camera.limits.radiusMax = config.radiusMax; }
            if(config.pitchMin !== undefined) { camera.limits.pitchMin = config.pitchMin; }
            if(config.pitchMax !== undefined) { camera.limits.pitchMax = config.pitchMax; }
        }
    }

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Process lights: apply YAML overrides to imported lights and create new ones.
     * If no YAML lights config exists, imported lights are left unchanged.
     */
    private _processLights(scene : Scene) : void
    {
        const lightConfigs = this._config.lights;
        if(!lightConfigs) { return; }

        for(const [ name, config ] of Object.entries(lightConfigs))
        {
            const existing = scene.getLightByName(name);

            if(existing)
            {
                this._applyLightConfig(existing, config);
                this._log.debug(`Applied overrides to light: "${ name }"`);
            }
            else if(config.type)
            {
                this._createLight(name, config, scene);
                this._log.debug(`Created ${ config.type } light: "${ name }"`);
            }
            else
            {
                this._log.warn(
                    `Light "${ name }" not found in scene and no type specified — skipping`
                );
            }
        }
    }

    /**
     * Create a new light from a YAML definition.
     */
    private _createLight(name : string, config : LightDefinition, scene : Scene) : void
    {
        const dir = config.direction ? toVector3(config.direction) : new Vector3(0, -1, 0);
        const pos = config.position ? toVector3(config.position) : Vector3.Zero();

        switch (config.type)
        {
            case 'hemispheric':
            {
                const light = new HemisphericLight(name, dir, scene);

                if(config.groundColor)
                {
                    light.groundColor = this._toColor3(config.groundColor);
                }

                this._applyLightConfig(light, config);
                break;
            }

            case 'directional':
            {
                const light = new DirectionalLight(name, dir, scene);
                this._applyLightConfig(light, config);
                break;
            }

            case 'point':
            {
                const light = new PointLight(name, pos, scene);
                this._applyLightConfig(light, config);
                break;
            }

            case 'spot':
            {
                const light = new SpotLight(
                    name,
                    pos,
                    dir,
                    config.angle ?? Math.PI / 3,
                    config.exponent ?? 2,
                    scene
                );
                this._applyLightConfig(light, config);
                break;
            }

            case 'rectarea':
            {
                const light = new RectAreaLight(
                    name,
                    pos,
                    config.width ?? 1,
                    config.height ?? 1,
                    scene
                );
                this._applyLightConfig(light, config);

                // RectAreaLight emits along local -Z and has no direction property. If a direction
                // is specified, parent the light to a rotated pivot node that orients -Z toward it.
                if(config.direction)
                {
                    const pivot = new TransformNode(`${ name }_pivot`, scene);
                    pivot.position = light.position.clone();
                    light.position = Vector3.Zero();
                    light.parent = pivot;

                    // lookAt aligns +Z toward the target; we want -Z toward dir, so look opposite
                    pivot.lookAt(pivot.position.subtract(dir));
                }

                break;
            }
        }
    }

    /**
     * Apply shared light properties from a config to an existing light.
     */
    private _applyLightConfig(
        light : { intensity : number; diffuse : Color3; specular : Color3 },
        config : LightDefinition
    ) : void
    {
        if(config.intensity !== undefined) { light.intensity = config.intensity; }
        if(config.diffuse) { light.diffuse = this._toColor3(config.diffuse); }
        if(config.specular) { light.specular = this._toColor3(config.specular); }
    }

    /**
     * Set up clustered lighting: create a container, move eligible lights into it,
     * and fix PBR material falloff for compatibility.
     */
    private _setupClustering(scene : Scene) : void
    {
        const clusterConfig = this._config.clustering;
        if(!clusterConfig?.enabled)
        {
            return;
        }

        // Collect eligible lights from the scene
        const eligibleLights = scene.lights.filter(
            (light) => ClusteredLightContainer.IsLightSupported(light)
        );

        if(eligibleLights.length === 0)
        {
            this._log.debug('Clustering enabled but no eligible lights found');
            return;
        }

        // Remove eligible lights from the scene before adding to container
        for(const light of eligibleLights)
        {
            scene.removeLight(light);
        }

        // Create the container with collected lights
        const container = new ClusteredLightContainer('sage-clustered', eligibleLights, scene);

        // Apply tuning parameters
        if(clusterConfig.horizontalTiles !== undefined) { container.horizontalTiles = clusterConfig.horizontalTiles; }
        if(clusterConfig.verticalTiles !== undefined) { container.verticalTiles = clusterConfig.verticalTiles; }
        if(clusterConfig.depthSlices !== undefined) { container.depthSlices = clusterConfig.depthSlices; }
        if(clusterConfig.maxRange !== undefined) { container.maxRange = clusterConfig.maxRange; }

        this.clusteredLights = container;

        this._log.debug(
            `Clustered lighting enabled: ${ eligibleLights.length } lights, `
            + `${ container.horizontalTiles }x${ container.verticalTiles } tiles, `
            + `${ container.depthSlices } depth slices`
        );

        // Fix PBR material falloff — physical falloff is incompatible with clustering
        for(const material of scene.materials)
        {
            if(material instanceof PBRMaterial)
            {
                material.useGLTFLightFalloff = true;
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------

    private _toColor3(color : ColorConfig) : Color3
    {
        return new Color3(color.r, color.g, color.b);
    }

    /**
     * Configure and enable the Havok physics plugin with the level's gravity settings.
     */
    private async _enablePhysics(scene : Scene) : Promise<void>
    {
        const physicsConfig = this._config.physics;

        // Determine gravity
        let gravity = new Vector3(0, -9.81, 0); // Default gravity

        if(typeof physicsConfig === 'object' && physicsConfig.gravity)
        {
            gravity = new Vector3(
                physicsConfig.gravity.x,
                physicsConfig.gravity.y,
                physicsConfig.gravity.z
            );
        }

        // When large world rendering is active, enable Havok multi-region physics
        const floatingOriginRadius = this.gameEngine.largeWorldRendering
            ? 100000
            : undefined;

        this._log.debug(`Enabling physics with gravity: ${ gravity.toString() }`);
        await this.gameEngine.engines.sceneEngine.enablePhysics(scene, gravity, floatingOriginRadius);
    }

    /**
     * Walk all transform nodes and meshes, normalizing glTF metadata and dispatching to property handlers.
     */
    private async _processNodeProperties(scene : Scene) : Promise<void>
    {
        const nodes = scene.transformNodes.concat(scene.meshes as TransformNode[]);

        for(const node of nodes)
        {
            // Normalize metadata - glTF extras are in node.metadata.gltf.extras
            const metadata = this._getNormalizedMetadata(node);

            if(metadata && Object.keys(metadata).length > 0)
            {
                // Store normalized metadata back on node for property handlers
                node.metadata = { ...node.metadata, ...metadata };

                // eslint-disable-next-line no-await-in-loop
                await this._processNodeMetadata(node);
            }
        }

        this._log.debug(
            `Processed ${ nodes.length } nodes, found ${ this._spawnPoints.length } spawn points `
            + `and ${ this._entityNodes.length } entity nodes`
        );
    }

    /**
     * Get normalized metadata from a node, handling glTF extras.
     *
     * Babylon.js stores glTF custom properties in node.metadata.gltf.extras,
     * so we need to extract them for easier access.
     */
    private _getNormalizedMetadata(node : TransformNode) : Record<string, unknown> | null
    {
        if(!node.metadata)
        {
            return null;
        }

        // Check for glTF extras (from imported GLB/GLTF files)
        const gltfExtras = node.metadata?.gltf?.extras as Record<string, unknown> | undefined;

        if(gltfExtras)
        {
            // Merge glTF extras with any existing top-level metadata
            return { ...gltfExtras };
        }

        // Return existing metadata if no glTF extras
        return node.metadata as Record<string, unknown>;
    }

    /**
     * Check a node's metadata for spawn/entity markers and run any registered property handlers.
     */
    private async _processNodeMetadata(node : TransformNode) : Promise<void>
    {
        // Normalize the spawn marker into canonical Babylon space. glTF imports
        // wrap content under a conversion root (RH→LH), and decomposing a
        // mirrored matrix produces poisoned scaling/rotation. getCanonicalTransform
        // cancels the import root before decomposing, giving clean values that
        // work uniformly for primitives and GLB entities.
        if('spawn' in node.metadata)
        {
            const { position, rotation, scaling } = getCanonicalTransform(node);

            this._spawnPoints.push({
                name: node.metadata.spawn as string,
                position,
                rotation,
                scaling,
                node,
            });
        }

        // Check for entity property
        if('entity' in node.metadata)
        {
            this._entityNodes.push({
                type: node.metadata.entity as string,
                node,
            });
        }

        // Run registered property handlers
        await this._runPropertyHandlers(node);
    }

    /**
     * Run registered property handlers on a node.
     */
    private async _runPropertyHandlers(node : TransformNode) : Promise<void>
    {
        for(const [ property, handler ] of this.propertyHandlers)
        {
            if(property in node.metadata)
            {
                try
                {
                    // eslint-disable-next-line no-await-in-loop
                    await handler(node, node.metadata[property], this, this.gameEngine);
                }
                catch (error)
                {
                    this._log.error(
                        `Error in property handler '${ property }' for node '${ node.name }':`,
                        error
                    );
                }
            }
        }
    }

    /**
     * Match collected spawn points against the level config's spawn definitions and instantiate entities.
     */
    private async _processSpawnPoints() : Promise<void>
    {
        for(const spawnPoint of this._spawnPoints)
        {
            const spawnDef = this._config.spawns?.[spawnPoint.name];

            if(!spawnDef)
            {
                this._log.warn(`No spawn definition found for '${ spawnPoint.name }', skipping`);
            }
            else
            {
                // eslint-disable-next-line no-await-in-loop
                await this._processSpawnPoint(spawnPoint, spawnDef);
            }
        }
    }

    /**
     * Spawn an entity at the given spawn point and dispose of the placeholder node.
     */
    private async _processSpawnPoint(spawnPoint : SpawnPointData, spawnDef : SpawnDefinition) : Promise<void>
    {
        try
        {
            const entity = await this._spawnEntity(spawnDef, spawnPoint);
            this._spawnedEntities.push(entity);

            this._applyDebugCollider(entity, spawnDef.debugCollider);

            // Remove the placeholder node from the scene
            spawnPoint.node.dispose();

            this._log.debug(`Spawned entity '${ spawnDef.entity }' at spawn point '${ spawnPoint.name }'`);
        }
        catch (error)
        {
            this._log.error(`Failed to spawn entity at spawn point '${ spawnPoint.name }':`, error);
        }
    }

    /**
     * Apply debug collider visualization to an entity if configured.
     * Level/spawn config takes priority over the entity definition's debugCollider.
     */
    private _applyDebugCollider(entity : GameEntity, levelDebugConfig ?: ColliderDebugConfig) : void
    {
        const entityDefConfig = this.gameEngine.managers.entityManager
            .getDefinition(entity.type)?.debugCollider;
        const debugConfig = levelDebugConfig ?? entityDefConfig;

        if(debugConfig)
        {
            entity.showCollider(debugConfig);
        }
    }

    /**
     * Create an entity from a spawn definition, inheriting position/rotation/scaling from the spawn point.
     */
    private async _spawnEntity(spawnDef : SpawnDefinition, spawnPoint : SpawnPointData) : Promise<GameEntity>
    {
        const initialState = {
            ...spawnDef.config,
            position: toVec3Object(spawnPoint.position),
            rotation: toQuatObject(spawnPoint.rotation),
            scaling: toVec3Object(spawnPoint.scaling),
        };

        const entity = await this.gameEngine.managers.entityManager.createEntity(spawnDef.entity, {
            name: spawnDef.name,
            tags: spawnDef.tags,
            initialState,
        });

        // Create mesh if the entity definition specifies one
        await this._createEntityMesh(entity, spawnPoint);

        return entity;
    }

    /**
     * Build or import a mesh from the entity definition and attach it to the entity.
     * Supports primitive shapes (box/sphere/capsule/cylinder) and GLB/GLTF file paths.
     */
    private async _createEntityMesh(entity : GameEntity, spawnPoint : SpawnPointData) : Promise<void>
    {
        if(!this._scene)
        {
            return;
        }

        // Get the entity definition to check for mesh config
        const definition = this.gameEngine.managers.entityManager.getDefinition(entity.type);
        if(!definition?.mesh)
        {
            return;
        }

        const meshConfig = definition.mesh;
        const scene = this._scene;

        const node = new TransformNode(`entity-${ entity.id }`, scene);

        // Create or import meshes based on source type
        let meshes : AbstractMesh[];

        switch (meshConfig.source)
        {
            case 'box':
                meshes = [ MeshBuilder.CreateBox(`${ entity.type }-mesh`, {
                    size: meshConfig.params?.size ?? 1,
                    width: meshConfig.params?.width,
                    height: meshConfig.params?.height,
                    depth: meshConfig.params?.depth,
                }, scene) ];
                break;

            case 'sphere':
                meshes = [ MeshBuilder.CreateSphere(`${ entity.type }-mesh`, {
                    diameter: meshConfig.params?.diameter ?? 1,
                    segments: meshConfig.params?.segments ?? 16,
                }, scene) ];
                break;

            case 'capsule':
                meshes = [ MeshBuilder.CreateCapsule(`${ entity.type }-mesh`, {
                    height: meshConfig.params?.height ?? 1.8,
                    radius: meshConfig.params?.radius ?? 0.4,
                }, scene) ];
                break;

            case 'cylinder':
                meshes = [ MeshBuilder.CreateCylinder(`${ entity.type }-mesh`, {
                    height: meshConfig.params?.height ?? 1,
                    diameter: meshConfig.params?.diameter ?? 1,
                }, scene) ];
                break;

            default:
            {
                // GLB/GLTF file path — import all meshes from the file
                const result = await this.gameEngine.engines.sceneEngine.importMeshes([], meshConfig.source, scene);

                if(result.meshes.length === 0)
                {
                    this._log.warn(`No meshes loaded from: ${ meshConfig.source }`);
                    node.dispose();
                    return;
                }

                meshes = result.meshes;

                // Re-parent root-level imported transform nodes under the entity node
                for(const tn of result.transformNodes as TransformNode[])
                {
                    if(!tn.parent)
                    {
                        tn.parent = node;
                    }
                }

                break;
            }
        }

        // Parent any orphan meshes under the entity node.
        // Primitives are always orphans; GLB meshes are already parented in their hierarchy.
        for(const mesh of meshes)
        {
            if(!mesh.parent)
            {
                mesh.parent = node;
            }
        }

        // Spawn transforms are already normalized to canonical Babylon space
        // (source __root__ stripped at collection time). Apply directly —
        // the entity's own __root__ (if GLB) handles its handedness conversion.
        node.position.copyFrom(spawnPoint.position);
        node.rotationQuaternion = spawnPoint.rotation.clone();
        node.scaling.copyFrom(spawnPoint.scaling);

        // Entity definition scale overrides spawn point scaling
        if(meshConfig.scale)
        {
            if(typeof meshConfig.scale === 'number')
            {
                node.scaling.setAll(meshConfig.scale);
            }
            else
            {
                node.scaling.set(meshConfig.scale.x, meshConfig.scale.y, meshConfig.scale.z);
            }
        }

        // Apply material override (typically used for primitives; GLB files usually bring their own materials)
        const matConfig = meshConfig.material;
        if(matConfig?.color)
        {
            const color = matConfig.color;

            if(matConfig.type === 'pbr')
            {
                const material = new PBRMaterial(`${ entity.type }-material`, scene);
                material.albedoColor = this._toColor3(color);

                if(matConfig.emissive)
                {
                    material.emissiveColor = this._toColor3(matConfig.emissive);
                }

                material.metallic = matConfig.metallic ?? 0;
                material.roughness = matConfig.roughness ?? 1;

                for(const mesh of meshes)
                {
                    mesh.material = material;
                }
            }
            else
            {
                const material = new StandardMaterial(`${ entity.type }-material`, scene);
                material.diffuseColor = this._toColor3(color);

                if(matConfig.emissive)
                {
                    material.emissiveColor = this._toColor3(matConfig.emissive);
                }

                for(const mesh of meshes)
                {
                    mesh.material = material;
                }
            }
        }

        // Process property handlers (colliders, etc.) on imported nodes.
        // These don't go through _processNodeProperties automatically.
        // Use getDescendants() to avoid duplicates from meshes + getChildren() overlap.
        const descendants = node.getDescendants(false)
            .filter((child) : child is TransformNode => child instanceof TransformNode);

        for(const imported of descendants)
        {
            const metadata = this._getNormalizedMetadata(imported);

            if(metadata && Object.keys(metadata).length > 0)
            {
                imported.metadata = {
                    ...imported.metadata,
                    ...metadata,
                };

                // eslint-disable-next-line no-await-in-loop
                await this._runPropertyHandlers(imported);
            }
        }

        // Attach the node to the entity
        this.gameEngine.managers.entityManager.attachToNode(entity, node);
    }

    /**
     * Create game entities for all nodes marked with the 'entity' metadata property.
     */
    private async _processEntityNodes() : Promise<void>
    {
        for(const entityNode of this._entityNodes)
        {
            // eslint-disable-next-line no-await-in-loop
            await this._processEntityNode(entityNode);
        }
    }

    /**
     * Create a game entity for an entity-tagged node, attaching the existing scene node to it.
     */
    private async _processEntityNode(entityNode : EntityNodeData) : Promise<void>
    {
        const entityDef = this._config.entities?.[entityNode.type];

        try
        {
            const initialState = {
                ...entityDef?.config,
                position: toVec3Object(entityNode.node.position),
            };

            const entity = await this.gameEngine.managers.entityManager.createEntity(entityNode.type, {
                name: entityDef?.name ?? entityNode.node.name,
                tags: entityDef?.tags,
                initialState,
                node: entityNode.node,
            });
            this._spawnedEntities.push(entity);

            this._applyDebugCollider(entity, entityDef?.debugCollider);

            this._log.debug(`Created entity '${ entityNode.type }' for node '${ entityNode.node.name }'`);
        }
        catch (error)
        {
            this._log.error(`Failed to create entity for node '${ entityNode.node.name }':`, error);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Level Sounds
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create level-scoped sounds from YAML config. Sounds are tracked for lifecycle management
     * (pause on deactivate, resume on activate, dispose on unload).
     */
    private async _processLevelSounds() : Promise<void>
    {
        const soundConfigs = this._config.sounds;
        if(!soundConfigs) { return; }

        const { audioManager } = this.gameEngine.managers;
        if(!audioManager)
        {
            this._log.warn('No AudioManager configured. Level sounds will not be created.');
            return;
        }

        for(const [ name, config ] of Object.entries(soundConfigs))
        {
            try
            {
                // eslint-disable-next-line no-await-in-loop
                const sound = await audioManager.createSound(name, config.url, config.channel, {
                    loop: config.loop ?? false,
                    volume: config.volume ?? 1,
                });

                this._levelSounds.set(name, sound);

                if(config.autoplay)
                {
                    sound.play();
                }

                this._log.debug(`Created level sound: "${ name }"`);
            }
            catch (error)
            {
                this._log.error(`Failed to create level sound "${ name }":`, error);
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Pause all playing level sounds when the level is deactivated.
     * Subclasses that override this must call `super.onDeactivate()`.
     */
    async onDeactivate() : Promise<void>
    {
        for(const [ name, sound ] of this._levelSounds)
        {
            if(sound.state === SoundState.Started)
            {
                this._playingSoundsBeforeDeactivate.add(name);
                sound.pause();
            }
        }
    }

    /**
     * Resume previously playing level sounds when the level is reactivated.
     * Subclasses that override this must call `super.onActivate()`.
     */
    async onActivate() : Promise<void>
    {
        for(const name of this._playingSoundsBeforeDeactivate)
        {
            const sound = this._levelSounds.get(name);
            if(sound)
            {
                sound.play();
            }
        }

        this._playingSoundsBeforeDeactivate.clear();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Dispose of this level's resources
     */
    override async $dispose() : Promise<void>
    {
        // Destroy all spawned entities
        const { entityManager } = this.gameEngine.managers;
        const destroyPromises = this._spawnedEntities.map(async (entity) =>
        {
            try
            {
                await entityManager.destroyEntity(entity.id);
            }
            catch (error)
            {
                this._log.error(`Failed to destroy entity ${ entity.id }:`, error);
            }
        });

        await Promise.all(destroyPromises);

        this._spawnedEntities = [];
        this._spawnPoints = [];
        this._entityNodes = [];

        // Dispose level sounds
        for(const sound of this._levelSounds.values())
        {
            sound.dispose();
        }
        this._levelSounds.clear();
        this._playingSoundsBeforeDeactivate.clear();

        await super.$dispose();
    }
}

//----------------------------------------------------------------------------------------------------------------------
